import React, { useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { validateEmail, validateName, formatToCurrency } from '../../../../helper/form';
import { validateIdentifierRepeat, validateEmailRepeat } from './validator';
import { isAuthorizedCustomFieldToUser } from "../../../../helper/custom_field/helper";
import _ from 'lodash-es';
import moment from 'moment';

const LaboralBulkLoadEmployeeTable = ({
  data,
  dataKey = "id",
  customFieldsHeaders = [],
  currentUser,
  securityLayers = [],
  actionsRenderer,
  onDataChange,
  title,
  showErrors = false,
  includeSecurityLayer = false
}) => {
  const onChangeCellValues = (value, row, key, customField) => {
    if (!value) {
      return;
    }

    if (customField?.data_type == 'currency') {
      value = formatToCurrency(value);
    }

    if (customField?.data_type == 'date') {
      value = moment(value, "YYYY-MM-DD").locale('es').format('DD/MM/YYYY');
    }

    if (onDataChange) {
      onDataChange(value, row, key, customField);
    }
  };

  const customFieldSelectOptions = useCallback((listOptions) => {
    return listOptions.map((option) => ({
      label: option,
      value: option
    }));
  }, []);

  const securityLayerOptions = () => {
    return _.map(securityLayers, security_layer => ({
      label: security_layer,
      value: security_layer
    }));
  };

  const renderSecurityLayerEditor = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={securityLayerOptions()}
        onChange={(e) => {
          options.editorCallback(e.value);
          onChangeCellValues(e.value, options.rowData, "security_layer");
        }}
        placeholder="Seleccionar capa de seguridad"
      />
    );
  };

  const renderIdentifierEditor = (options) => {
    const valRepeatId = showErrors ? validateIdentifierRepeat(options.value, data) : false;

    return (
      <div>
        <InputText
          value={options.value}
          onChange={(e) => {
            options.editorCallback(e.target.value);
            onChangeCellValues(e.target.value, options.rowData, "identifier");
          }}
          placeholder="Ingrese identificador"
          className={valRepeatId ? 'p-invalid' : ''}
        />
        {valRepeatId && (
          <small className="p-error">
            {I18n.t('activerecord.errors.models.laboral/employee.attributes.identifier.taken')}
          </small>
        )}
      </div>
    );
  };

  const renderEmailEditor = (options) => {
    const valEmail = showErrors && !validateEmail(options.value);
    const valRepeatEmail = showErrors ? validateEmailRepeat(options.value, data) : false;

    return (
      <div>
        <InputText
          value={options.value}
          onChange={(e) => {
            options.editorCallback(e.target.value);
            onChangeCellValues(e.target.value, options.rowData, "value");
          }}
          placeholder="Ingrese email"
          className={valEmail || valRepeatEmail ? 'p-invalid' : ''}
        />
        {valEmail && (
          <small className="p-error">
            {I18n.t('activerecord.errors.models.laboral/employee_config.attributes.email.invalid_format')}
          </small>
        )}
        {!valEmail && valRepeatEmail && (
          <small className="p-error">
            {I18n.t('activerecord.errors.models.laboral/employee_config.attributes.email.taken')}
          </small>
        )}
      </div>
    );
  };

  const renderNameEditor = (options) => {
    const valName = showErrors && !validateName(options.value);

    return (
      <div>
        <InputText
          value={options.value}
          onChange={(e) => {
            options.editorCallback(e.target.value);
            onChangeCellValues(e.target.value, options.rowData, "name");
          }}
          placeholder="Ingrese nombre"
          className={valName ? 'p-invalid' : ''}
        />
        {valName && (
          <small className="p-error">
            {I18n.t('activerecord.errors.models.laboral/employee_config.attributes.name.blank')}
          </small>
        )}
      </div>
    );
  };

  const renderCustomFieldEditor = (options, customField) => {
    if (customField.data_type === "list") {
      const optionsList = customFieldSelectOptions(customField.list_options);
      return (
        <Dropdown
          value={options.value}
          options={optionsList}
          onChange={(e) => {
            options.editorCallback(e.value);
            onChangeCellValues(e.value, options.rowData, customField.code, customField);
          }}
          disabled={!isAuthorizedCustomFieldToUser(customField, currentUser)}
          placeholder={`Seleccionar ${customField.code}`}
        />
      );
    } else {
      return (
        <InputText
          value={options.value}
          onChange={(e) => {
            options.editorCallback(e.target.value);
            onChangeCellValues(e.target.value, options.rowData, customField.code, customField);
          }}
          disabled={!isAuthorizedCustomFieldToUser(customField, currentUser)}
          placeholder={`Ingrese ${customField.code}`}
        />
      );
    }
  };

  const renderActions = (rowData) => {
    if (actionsRenderer) {
      return actionsRenderer(rowData);
    }
    return null;
  };

  const editingRows = _.reduce(
    data,
    (accumulator, employee) => ({
      ...accumulator,
      [employee[dataKey]]: true
    }),
    {}
  );

  return (
    <div className="row pt-2">
      {title && <div className="mb-2">{title}</div>}

      <DataTable
        value={data}
        editMode="row"
        editingRows={editingRows}
        onRowEditChange={() => { }} // Si no se define entonces editingRows no funciona.
        size="small"
        dataKey={dataKey}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
        emptyMessage={I18n.t('laboral.bulk.load.employees.form.without_records')}
        scrollable
        scrollHeight="600px"
        className="p-datatable-striped"
      >
        {includeSecurityLayer && (
          <Column
            field="security_layer"
            header="Capa de seguridad"
            editor={(options) => renderSecurityLayerEditor(options)}
            sortable
            filter
            showAddButton={false}
            filterPlaceholder="Buscar capa de seguridad"
          />
        )}
        <Column
          field="identifier"
          header={I18n.t('activerecord.attributes.laboral/employee.identifier')}
          editor={(options) => renderIdentifierEditor(options)}
          sortable
          filter
          showAddButton={false}
          filterPlaceholder={`Buscar ${I18n.t('activerecord.attributes.laboral/employee.identifier')}`}
        />
        <Column
          field="value"
          header={I18n.t('activerecord.attributes.laboral/employee.email')}
          editor={(options) => renderEmailEditor(options)}
          sortable
          filter
          showAddButton={false}
          filterPlaceholder={`Buscar ${I18n.t('activerecord.attributes.laboral/employee.email')}`}
        />
        <Column
          field="name"
          header={I18n.t('activerecord.attributes.laboral/employee.name')}
          editor={(options) => renderNameEditor(options)}
          sortable
          filter
          showAddButton={false}
          filterPlaceholder={`Buscar ${I18n.t('activerecord.attributes.laboral/employee.name')}`}
        />
        {_.map(customFieldsHeaders, (customField) => {
          const isAuthorizedCustomField = isAuthorizedCustomFieldToUser(customField, currentUser);

          return (
            <Column
              key={customField.code}
              field={customField.code}
              header={customField.code}
              editor={(options) => renderCustomFieldEditor(options, customField)}
              sortable={isAuthorizedCustomField}
              filter={isAuthorizedCustomField}
              showAddButton={false}
              filterPlaceholder={`Buscar ${customField.code}`}
            />
          );
        })}
        {actionsRenderer && (
          <Column
            field="row"
            header={I18n.t("actions.actions")}
            body={renderActions}
            style={{ width: '100px' }}
          />
        )}
      </DataTable>
    </div>
  );
};

export default LaboralBulkLoadEmployeeTable;
